import { z } from 'zod';

export const orgRoleSchema = z.enum(['owner', 'admin', 'member', 'guest']);
export type OrgRole = z.infer<typeof orgRoleSchema>;

export const membershipStatusSchema = z.enum(['active', 'invited', 'deactivated']);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

export const orgPlanSchema = z.enum(['free', 'team']);
export type OrgPlan = z.infer<typeof orgPlanSchema>;

/**
 * Character rules for an organization slug (docs/04-architecture.md 4.11).
 *
 * Reserved-word blocking is a separate concern and lives in `@mapshare/core`;
 * this schema only fixes the shape. Both are required before a slug is accepted.
 */
export const orgSlugSchema = z
  .string()
  .min(3)
  .max(30)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, {
    message: 'slug must be lowercase alphanumeric with single internal hyphens',
  });

/**
 * `GET /api/v1/account/organizations` — the organizations the signed-in caller
 * can open, for the site's sign-in (`mapshare.fyi`, OQ-48).
 *
 * `url` is built by the Worker, which knows this deployment's tenant mode:
 * `https://<slug>.mapshare.fyi/` in host mode, `<origin>/<slug>/` in path mode.
 * `standing: 'invited'` is a pending invitation to the token's email; opening
 * `url` accepts it.
 */
export const accountOrganizationSchema = z.object({
  slug: orgSlugSchema,
  name: z.string(),
  role: orgRoleSchema,
  standing: z.enum(['member', 'invited']),
  url: z.string().url(),
});
export type AccountOrganizationView = z.infer<typeof accountOrganizationSchema>;

export const accountOrganizationsResponseSchema = z.object({
  organizations: z.array(accountOrganizationSchema),
  /**
   * A first value for the slug field when creating an organization (OQ-51),
   * made from the domain of the token's email — never its local part — or
   * `null` when that gives none (free mail, no email). The page prefers one
   * made from the name being typed (`suggestOrgSlug`, `@mapshare/core`), so
   * the email itself never has to reach it.
   */
  suggestedSlug: orgSlugSchema.nullable(),
});
export type AccountOrganizationsResponse = z.infer<typeof accountOrganizationsResponseSchema>;

/**
 * `POST /api/v1/account/organizations` — create an organization and become its
 * owner (2026-09-28, OQ-51). The slug is checked again on the Worker
 * (`checkOrgSlug()`), and a taken one is refused there.
 */
export const createOrganizationRequestSchema = z.object({
  name: z.string(),
  slug: z.string(),
});
export type CreateOrganizationRequest = z.infer<typeof createOrganizationRequestSchema>;

export const createOrganizationResponseSchema = z.object({
  organization: accountOrganizationSchema,
});
export type CreateOrganizationResponse = z.infer<typeof createOrganizationResponseSchema>;

/**
 * Why a creation was refused, next to the sentence the page shows (RFC 9457
 * extension member `reason`): the slug's format or a reserved word, a slug
 * someone has, or the creator's limit.
 */
export const createOrganizationRejectionSchema = z.enum([
  'invalid_name',
  'invalid_format',
  'reserved_word',
  'reserved_prefix',
  'slug_taken',
  'limit',
]);
export type CreateOrganizationRejection = z.infer<typeof createOrganizationRejectionSchema>;
